// @ts-check
import { differenceInHours, isFuture, format } from "date-fns";

import { pageDocPath } from "../../helpers";
import { STASHED_ATTRIBUTION_TIME_LIMIT_HOURS } from "../components/web-form";
import clientConfig from "../../client-config";
import imageUrlBuilder from "@sanity/image-url";

// Manually generated lqip (low-quality image placeholder) for /static/img/emerald-bg.jpg
// Used as a blur-hash stand-in for CSS background images while the full asset loads.
export const EMERALD_BG_LQIP = "data:image/jpeg;base64,/9j/2wBDAAYEBQYFBAYGBQYHBwYIChAKCgkJChQODwwQFxQYGBcUFhYaHSUfGhsjHBYWICwgIyYnKSopGR8tMC0oMCUoKSj/2wBDAQcHBwoIChMKChMoGhYaKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCj/wAARCAAoACgDASIAAhEBAxEB/8QAGwAAAQUBAQAAAAAAAAAAAAAAAAIDBAUGBwj/xAAqEAACAgIBAgUCBwAAAAAAAAABAgADBBEFEiEGMUFRYRQiExUyUmJygf/EABgBAAMBAQAAAAAAAAAAAAAAAAECAwAE/8QAIBEBAAIBBAIDAAAAAAAAAAAAAQACEQMEITESExRRYf/aAAwDAQACEQMRAD8A9HmsEfd3kfIwqrE0CUPuJKZT0+crcu1l+1dk/EBOe+A5lHmcYMfMNq3G0EDe+2jCjmvpWNKptmPcgd4rNfJYMK6m2PXUz9tWXTkNdZUzM3l8RznucF7eDms2FbfUVBuohiO4hKrEzLkpUBACfMtCaULCTVsV1vqEZPSTs6MzCchY2xVajN7nvK3lORz8dS2MVZv2kmE01jO4qc4m5ssrUBdAE/MiW4Skh7bx3nKsjxFzPX0nHesn1VSx/wA1FWeJ89VFf4FrWH1ubUoaFpJ3VHsnQb7MGmwjrLWfyhOY/mnLWnqK069gTuEb0/sn8g+pqsTDDNsMynXoYu/Ccv3diPkwhHkwMR+jjw3qdjy1JLcW1gHUgb+0IRFSUrQY23EJX+qje/YQhCYYGgM//9k=";

/**
 * @param  {...(string|null|undefined|false)} args
 * @returns {string}
 */
export function cn(...args) {
  return args.filter(Boolean).join(" ");
}

/**
 * @param {{edges?: {node?: unknown}[]}} data
 * @returns {unknown[]|null}
 */
export function mapEdgesToNodes(data) {
  if (!data.edges) return [];
  return data.edges.map((edge) => edge.node);
}

/**
 * @param {{slug?: Record<string, unknown>}} docs
 * @returns {unknown}
 */
export function filterOutDocsWithoutSlugs({ slug }) {
  return (slug || {}).current;
}

/**
 * @param {{publishedAt: string | number | Date }} published
 * @returns {boolean}
 */
export function filterOutDocsPublishedInTheFuture({ publishedAt }) {
  return !isFuture(new Date(publishedAt));
}

/**
 * Builds display labels for an invite's date/time, supporting an optional
 * end date (time range, or multi-day range) and all-day events.
 * @param {{startDate: string, endDate?: string, allDay?: boolean }} invite
 * @returns {{dateLabel: string|null, timeLabel: string|null}}
 */
export function getInviteDateLabels({ startDate, endDate, allDay }) {
  if (!startDate) return { dateLabel: null, timeLabel: null };

  const start = new Date(startDate);
  const end = endDate ? new Date(endDate) : null;
  const sameDay = end && start.toDateString() === end.toDateString();

  const dateLabel = end && !sameDay
    ? `${format(start, "MMM d")} – ${format(end, "MMM d, yyyy")}`
    : format(start, "MMM d, yyyy");

  const timeLabel = allDay || (end && !sameDay)
    ? null
    : end
      ? `${format(start, "h:mm a")} – ${format(end, "h:mm a")}`
      : format(start, "h:mm a");

  return { dateLabel, timeLabel };
}

/**
 *
 * @param {string} string
 * @returns {string}
 */
export const toFirstCapital = (string) =>
  string.charAt(0).toUpperCase() + string.slice(1);

const CARD_SUMMARY_MAX = 310;
const TITLE_MULTIPLIER = 1.5;

/**
 * @param {{ str: string, titleStr: string, max: number }} data
 * @returns {string}
 */
export const truncateText = ({ str, titleStr, max = CARD_SUMMARY_MAX }) => {
  const textLength = titleStr
    ? Math.round(max - titleStr.length * TITLE_MULTIPLIER)
    : max;

  if (textLength < 0) {
    return "";
  }

  const trimmed = str.slice(0, textLength);

  return trimmed.length < textLength
    ? trimmed
    : trimmed.substr(0, Math.min(trimmed.length, trimmed.lastIndexOf(" "))) +
    "...";
};

/**
 *
 * @param {{
 *  crop?: unknown,
 *  hotspot?: unknown,
 *  asset: Record<string, unknown>
 * }=} source
 * @returns {{
 *  asset: { _ref: unknown },
 *  crop?: unknown,
 *  hotspot?: unknown
 * }|null}
 */
export function buildImageObj(source = { asset: {} }) {
  if (!source.asset) {
    return null;
  }

  /**
   * @type {{ asset: { _ref: unknown }, crop?: unknown, hotspot?: unknown}}
   */
  const imageObj = {
    asset: { _ref: source.asset._ref || source.asset._id },
  };

  if (source.crop) imageObj.crop = source.crop;
  if (source.hotspot) imageObj.hotspot = source.hotspot;

  return imageObj;
}

/**
 * @param {{
 *  crop?: unknown,
 *  hotspot?: unknown,
 *  asset: Record<string, unknown>
 * }=} source
 * @returns {imageUrlBuilder|null}
 */
export function imageBuilder(source) {
  const builder = imageUrlBuilder(clientConfig.sanity);
  const obj = buildImageObj(source);
  // @ts-ignore
  return obj ? builder.image(obj) : null;
}

/**
 * @param {{_type: string, children?: {text: string}[]}[] | null} blocks
 * @returns {string}
 */
export function toPlainText(blocks) {
  return blocks
    ? blocks
      .map((block) =>
        block._type !== "block" || !block.children
          ? ""
          : block.children.map((child) => child.text).join("")
      )
      .join("\n\n")
    : "";
}

/**
 * @param {string} text
 * @returns {object}
 */
export const toTextBlock = (text, mark = "p") => {
  return [
    {
      _type: "block",
      children: [{ _type: "span", marks: [], text }],
      markDefs: [],
      style: mark,
    },
  ];
};

/**
 * @param {string} hex
 * @returns {{r: number, g: number, b: number} | null}
 */
export function hexToRgb(hex) {
  var result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16),
    }
    : null;
}

/**
 * @param {string} email
 * @returns {boolean}
 */
export const isEmail = (email) =>
  /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email);

/**
 * Only for client-side interactions.
 *
 * @param {string} href
 * @returns {string}
 */
const isBrowser = typeof window !== "undefined";
export const currentPath = (
  href = isBrowser ? window.location.pathname : ""
) => {
  return href.replace(/\/$/, "").replace(/^\//, "") || "";
};

/**
 * @param {string} text
 */
export const capitalizeFirstLetter = (text) => {
  return text.charAt(0).toUpperCase() + text.slice(1);
};

// Make this a page setting
const SLIM_PATHS = [
  "/join-slack/",
  "/hello/pipeline/",
  "/hello/cmo/",
  "/hello/dark-funnel-demand/",
  "/hello/discover-pipeline-dark-funnel/",
  "/hello/growth-hacks/",
  "/hello/demand-curve/",
  "/referral-program/",
  "/series/mavericks/",
  "/series/101/",
  "/blog/",
];
/**
 * @param {*} site
 * @returns {boolean}
 */
export const isThinPage = (site) =>
  SLIM_PATHS.includes(pageDocPath(site?.doc)) ||
  [
    "playbook",
    "company",
    "integration",
    "story",
    "post",
    "signal",
    "redirect",
    "prompt",
    "asset",
    "agent",
    "competeDB",
    "scenario",
    "trainingCourse",
    "glossary",
    "author",
  ].includes(site?.doc?._type) ||
  ["narrow", "thin", "large"].includes(site?.doc?.pageWidth);

/**
 * Handle true in session and local storage.
 *
 * @param {*} value
 * @returns {boolean}
 */
export const dualTypeTrue = (value) => value === true || value === "true";

/**
 * Set localStorage value plus created date and origin value
 * @param {{storage: *, value: string, id: string, skipOrigin: boolean}} param0
 */
export const setStashed = ({ id, value, storage, skipOrigin }) => {
  if (!skipOrigin && (!storage.getItem(id) || storage.getItem(id) === "")) {
    storage.setItem(`origin.${id}`, value);
    storage.setItem(`origin.${id}.created`, new Date().toISOString());
  }

  storage.setItem(id, value);
  storage.setItem(`${id}.created`, new Date().toISOString());
};

/**
 * Get localStorage value when within time window
 * @param {{storage: *, id: string}} param0
 * @returns {string}
 */
export const getStashed = ({ storage, id }) => {
  const created = storage.getItem(`${id}.created`);
  const stashedVal = storage.getItem(id);

  return differenceInHours(new Date(), new Date(created)) <
    STASHED_ATTRIBUTION_TIME_LIMIT_HOURS
    ? stashedVal
    : null;
};

/**
 * @param {{storage: *, id: string}} param0
 */
export const removeStashed = ({ id, storage }) => {
  storage.removeItem(id);
  storage.removeItem(`${id}.created`);
};

/**
 *
 * @param {string} str
 */
export const hashCode = (str) => {
  return Math.abs(
    Array.from(str).reduce(
      (s, c) => (Math.imul(31, s) + c.charCodeAt(0)) | 0,
      0
    )
  );
};

/**
 * Parses a freeform authored duration string ("2:13", "12:05", "1:02:34")
 * into total seconds. Returns null if the string doesn't cleanly match
 * m:ss or h:mm:ss (e.g. "6 min", empty, or otherwise malformed authored data).
 * @param {string} duration
 * @returns {number|null}
 */
export function parseDurationToSeconds(duration) {
  if (!duration || typeof duration !== "string") return null;
  const parts = duration.trim().split(":");
  if (parts.length < 2 || parts.length > 3) return null;
  if (parts.some((p) => !/^\d{1,2}$/.test(p))) return null;

  const nums = parts.map((p) => parseInt(p, 10));
  const seconds = nums.pop();
  const minutes = nums.pop() ?? 0;
  const hours = nums.pop() ?? 0;
  if (seconds > 59 || minutes > 59) return null;

  return hours * 3600 + minutes * 60 + seconds;
}

/**
 * Formats a single training's duration for a card badge, e.g. "6 min".
 * Falls back to the raw authored string if it doesn't parse cleanly,
 * so authoring mistakes are visible rather than silently dropped.
 * @param {string} duration
 * @returns {string|null}
 */
export function formatDurationMinutes(duration) {
  if (!duration) return null;
  const totalSeconds = parseDurationToSeconds(duration);
  if (totalSeconds == null) return duration;
  const minutes = Math.max(1, Math.round(totalSeconds / 60));
  return `${minutes} min`;
}

/**
 * Sums an array of freeform duration strings, ignoring any that don't parse.
 * @param {(string|undefined)[]} durations
 * @returns {number}
 */
export function sumDurationsToSeconds(durations) {
  return (durations || []).reduce((total, d) => {
    const secs = parseDurationToSeconds(d);
    return secs != null ? total + secs : total;
  }, 0);
}

/**
 * Formats a total duration in seconds for a series meta line,
 * e.g. "18 min total" or "1 hr 23 min total".
 * @param {number} totalSeconds
 * @returns {string|null}
 */
export function formatTotalDuration(totalSeconds) {
  if (!totalSeconds) return null;
  const totalMinutes = Math.round(totalSeconds / 60);
  if (totalMinutes < 60) return `${totalMinutes} min total`;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return minutes > 0 ? `${hours} hr ${minutes} min total` : `${hours} hr total`;
}

/**
 * Formats a total duration in seconds as whole hours, e.g. "2 hrs" / "1 hr",
 * falling back to minutes for totals under an hour, e.g. "45 min".
 * Used for the top-level playlist stat pill, distinct from formatTotalDuration's
 * more precise per-series hr+min output.
 * @param {number} totalSeconds
 * @returns {string|null}
 */
export function formatTotalHours(totalSeconds) {
  if (!totalSeconds) return null;
  if (totalSeconds < 3600) {
    const minutes = Math.max(1, Math.round(totalSeconds / 60));
    return `${minutes} min`;
  }
  const hours = Math.round(totalSeconds / 3600);
  return hours === 1 ? "1 hr" : `${hours} hrs`;
}

// smooth-scrolls to an in-page anchor, clearing the sticky site nav (see invite.js)
export const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  const navHeight = document.getElementById("nav")?.getBoundingClientRect().height || 0;
  const top = el.getBoundingClientRect().top + window.scrollY - navHeight - 40;
  window.scrollTo({ top, behavior: "smooth" });
};
